import { UPLOAD_SECTIONS_IDS, fileTypes, DATA_CATEGORY_LABELS } from '../../constants';
import { CategoryData } from './OverviewDistrictCard';
import {
  calculateFlatDataUploadCategoryProgressBySchool,
  calculateNestedDataUploadCategoryProgressBySchool,
  calculateFlatDataUploadProgress,
  calculateNestedDataUploadProgress,
} from '../data upload/utilityFunctions';
import type {
  FlatUploadRequirementsData,
  NestedUploadRequirementsData,
} from '../../interfaces/UploadRequirementsInterface';
import { AxiosError } from 'axios';
import Schools from '../../interfaces/SchoolsInterface';
import type GlobalState from '../../interfaces/GlobalStateInterface';

interface RequestData {
  type: string;
  categoryName: CategoryData['categoryName'];
  errorMessage: string;
  anchorLink: CategoryData['anchorLink'];
  description: CategoryData['description'];
}

interface FlatRequestData extends RequestData {
  calculateUploadProgress: typeof calculateFlatDataUploadProgress;
}

interface NestedRequestData extends RequestData {
  calculateUploadProgress: typeof calculateNestedDataUploadProgress;
}

const uploadCategoryRequestsOrder: Array<FlatRequestData | NestedRequestData> = [
  {
    type: 'flat',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.STUDENT_ROSTER],
    errorMessage: 'Error occured while fetching student roster data!',
    anchorLink: `/data-upload#${UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_ROSTER]}`,
    calculateUploadProgress: calculateFlatDataUploadProgress,
    description: '0',
  },
  {
    type: 'nested',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG],
    errorMessage: 'Error occured while fetching course catalog data!',
    anchorLink: `/data-upload#${UPLOAD_SECTIONS_IDS[fileTypes.COURSE_CATALOG]}`,
    calculateUploadProgress: calculateNestedDataUploadProgress,
    description: '0',
  },
  {
    type: 'nested',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.STUDENT_DEMOGRAPHICS],
    errorMessage: 'Error occured while fetching student demographics data!',
    anchorLink: `/data-upload#${UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_DEMOGRAPHICS]}`,
    calculateUploadProgress: calculateNestedDataUploadProgress,
    description: '0',
  },
  {
    type: 'flat',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS],
    errorMessage: 'Error occured while fetching student transcripts data!',
    anchorLink: `/data-upload#${UPLOAD_SECTIONS_IDS[fileTypes.STUDENT_TRANSCRIPTS]}`,
    calculateUploadProgress: calculateFlatDataUploadProgress,
    description: '0',
  },
  {
    type: 'nested',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES],
    errorMessage: 'Error occured while fetching test scores data!',
    anchorLink: `/data-upload#${UPLOAD_SECTIONS_IDS[fileTypes.TEST_SCORES]}`,
    calculateUploadProgress: calculateNestedDataUploadProgress,
    description: '0',
  },
  {
    type: 'nested',
    categoryName: DATA_CATEGORY_LABELS[fileTypes.CTE_PATHWAYS],
    errorMessage: 'Error occured while fetching CTE pathways data!',
    anchorLink: `/data-upload#${UPLOAD_SECTIONS_IDS[fileTypes.CTE_PATHWAYS]}`,
    calculateUploadProgress: calculateNestedDataUploadProgress,
    description: '0',
  },
];

const filterExternalSchools = <T extends FlatUploadRequirementsData | NestedUploadRequirementsData>(
  data: T,
  schools?: Schools[],
  includeExternal: boolean = false
): T => {
  if (includeExternal || !schools) return data;

  const filteredSchools: typeof data.schools = {};
  const externalSchoolNames = new Set(
    schools.filter((school) => school.is_primary_on_eoa === false).map((school) => school.name)
  );

  Object.entries(data.schools).forEach(([schoolName, schoolData]) => {
    if (!externalSchoolNames.has(schoolName)) {
      filteredSchools[schoolName] = schoolData;
    }
  });

  return { ...data, schools: filteredSchools };
};

const calculateUploadProgress = (
  uploadRequirements: GlobalState['uploadRequirements'],
  handleError: (errorMessage: string | string[], error: Error | AxiosError) => void,
  schools?: Schools[]
) => {
  const categoriesByUploadStatus: GlobalState['uploadProgressSummary']['data'] = {
    pending: [],
    progress: [],
    completed: [],
    errors: [],
  };
  const uploadStatusByCategory: GlobalState['uploadProgressByCategory'] = {};
  const categoryUploadProgressBySchool: GlobalState['schoolsUploadProgressByCategory'] = {};
  const errorMessages: string[] = [];

  uploadCategoryRequestsOrder.forEach((request, index) => {
    let responseData = uploadRequirements[index];
    if (responseData.status === 'fulfilled') {
      let result;
      let schoolUploadProgress;
      const includeExternal = request.categoryName === DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG];

      if (request.type === 'flat') {
        const filteredData = filterExternalSchools(
          responseData.value.data as FlatUploadRequirementsData,
          schools,
          includeExternal
        );
        result = (request as FlatRequestData).calculateUploadProgress(filteredData);
        schoolUploadProgress = calculateFlatDataUploadCategoryProgressBySchool(filteredData);
      } else {
        const filteredData = filterExternalSchools(
          responseData.value.data as NestedUploadRequirementsData,
          schools,
          includeExternal
        );
        result = (request as NestedRequestData).calculateUploadProgress(filteredData);
        schoolUploadProgress = calculateNestedDataUploadCategoryProgressBySchool(filteredData);
      }

      categoriesByUploadStatus[result.state].push({
        categoryName: request.categoryName,
        anchorLink: request.anchorLink,
        description: result.description,
        uploadPercent: result.uploadPercent,
        error: false,
      });

      uploadStatusByCategory[request.categoryName] = {
        state: result.state,
        description: result.description,
      };

      categoryUploadProgressBySchool[request.categoryName] = schoolUploadProgress;
    } else {
      errorMessages.push(request.errorMessage);
      categoriesByUploadStatus.errors.push({
        categoryName: request.categoryName,
        anchorLink: request.anchorLink,
        description: 'Status unavailable',
        uploadPercent: 0,
        error: true,
      });
    }
  });

  if (errorMessages.length > 0) handleError(errorMessages, new Error('Upload requirement fetch error'));

  return { categoriesByUploadStatus, uploadStatusByCategory, categoryUploadProgressBySchool };
};

export { uploadCategoryRequestsOrder, calculateUploadProgress };
